import React from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import { useFormContext, Controller } from "react-hook-form";
import { useGetProjectsByNameQuery } from "services/project/projectApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebouncedCallback } from "use-debounce";
import { ProjectSlug } from "services/project/typing";
import { inputLabelStyles } from "./styles";

const ProjectDefinition = () => {
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly ProjectSlug[]>([]);
  // const [ searchResults, setSearchResults ] = React.useState<readonly string[]>([]);
  const loading = open && options.length === 0;

  //  The way the search works is that
  // 1. the User enter a value in to the Autcomplete TextField
  // 2. the onChange handler grabs the value from the textfield and sets the inputValue
  // 3. The first useEffect calls updateSearchValue which then calls debounce func on the inputValue
  // 4. The debounce func checks if the value !== '' and then updates the searchValue
  // 5. The change in the search value allows useGetProjectsByNamesQuery to trigger if searchValue !== '' and returns a response
  // 6. The returned response then cause setOptions to trigger, which calls the setOptions and fills the options dropdown
  const [inputValue, setInputValue] = React.useState<string>("");
  const [searchValue, setSearchValue] = React.useState<string>(inputValue);
  const [isTyping, setTyping] = React.useState(false);
  const { data, error, isFetching, isLoading } = useGetProjectsByNameQuery(
    searchValue ?? skipToken,
    { skip: searchValue === "" }
  );

  const debounced = useDebouncedCallback(
    (value) => {
      (async () => {
        if (value === "") {
          setTyping(false);
          return;
        }

        // if (loading) {
        //   // If loading, don't fire yet
        //   return;
        // }
        setSearchValue(value);
        setTyping(false);
      })();
    },

    500
  );

  const handleInputValueChange = (
    event: React.SyntheticEvent,
    value: string
  ) => {
    setTyping(true);
    setInputValue(value);
  };

  const handleValueChange = (
    event: React.SyntheticEvent,
    value: ProjectSlug | null
  ) => {
    if (!value) {
      return;
    }
    setValue("parentUid", value.uid);
    setValue("project", value.project);
  };

  React.useEffect(updateSearchValue, [inputValue, debounced]);
  React.useEffect(updateOptions, [data, isLoading]);

  function updateSearchValue() {
    debounced(inputValue);
  }

  function updateOptions() {
    if (isLoading) {
      return;
    }

    if (!data) {
      return;
    }

    setOptions(data);
  }

  return (
    <Grid xs={12}>
      <Stack spacing={5}>
        <input
          hidden
          type="text"
          placeholder="Project"
          {...register("parentUid", { required: false })}
        />
        <Autocomplete
          fullWidth
          inputValue={inputValue}
          id="asynchronous-demo"
          sx={{ width: 512 }}
          // open={open}
          // onOpen={() => {
          //   setOpen(true);
          // }}
          // onClose={() => {
          //   setOpen(false);
          // }}
          isOptionEqualToValue={(option, value) => option.title === value.title}
          getOptionLabel={(option) => option.title}
          onInputChange={handleInputValueChange}
          options={options}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                ".MuiInputBase-root": {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
              label="Parent Project"
              InputLabelProps={inputLabelStyles(theme)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isTyping || isFetching ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          {...register("project")}
          onChange={handleValueChange}
          // renderOption={() => "Fix Me Senpai!"}
        />
        <Grid container xs={12}>
          <FormControl>
            <Controller
              control={control}
              name="title"
              defaultValue=""
              // as={ <TextField id='title' /> }
              render={({ field }) => (
                <TextField
                  label="Title"
                  id="title"
                  sx={{
                    width: "512px",
                    // '.MuiOutlinedInput-notchedOutline': { border: `2px solid ${theme.palette.text.primary}`}
                    ".MuiInputBase-root": {
                      backgroundColor: theme.palette.background.paper,
                    },
                  }}
                  {...field}
                  InputLabelProps={inputLabelStyles(theme)}
                />
              )}
            />
          </FormControl>

          <Grid xs={4}>
            <Tooltip title={ "Set whether this project can be seen by users. Check this box when the project is released OR part of this project is released." }>
              <span>
                <FormControl>
                  <FormControlLabel
                    label="Accessible"
                    control={
                      <Controller
                        control={control}
                        name="accessible"
                        render={({ field }) => (
                          <Checkbox
                            sx={{
                              color: "#fff",
                              "&Mui-Checked": { color: "primary" },
                              "& .MuiSvgIcon-root": { fontSize: 32 },
                            }}
                            {...register("accessible")}
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        {/* <input type="text" placeholder="Title" {...register("title", {})} /> */}
      </Stack>
    </Grid>
  );
};

export default ProjectDefinition;
